import type { JSX } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DeactivateStudentButton } from '@/features/students/deactivate-student-button';
import { EditStudentDialog } from '@/features/students/edit-student-dialog';
import { useStudent } from '@/features/students/hooks';
import { GuardianSection } from '@/features/guardian/guardian-section';
import { ProgressSection } from '@/features/memorization/progress-section';
import { QuranProgressMap } from '@/features/memorization/quran-progress-map';
import { SubmissionList } from '@/features/memorization/submission-list';
import { TargetList } from '@/features/memorization/target-list';
import { MessageThreadCard } from '@/features/messaging/message-thread-card';
import { SessionList } from '@/features/murajaah/session-list';
import { PreferredMethodCard } from '@/features/students/preferred-method-card';
import { useHasCapability } from '@/lib/capabilities';

/**
 * The central student workspace (Phase G-1, Job 8): student information,
 * Guardian management (reused from Phase G-0, not duplicated), Hafalan
 * (Memorization Targets), and Progress. One surface for every role -
 * Teacher/Admin, Student (Self), and Guardian (Child, via Anak Saya) all
 * land here; visible controls are capability-driven, not role-branched.
 */
export function StudentDetailPage(): JSX.Element {
  const { studentId } = useParams<{ studentId: string }>();
  const { data: student, isLoading, isError } = useStudent(studentId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const canManage = useHasCapability('students:manage');

  if (studentId === undefined) {
    return <p className="p-4 text-sm text-destructive">Santri tidak ditemukan.</p>;
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 sm:p-6">
      <h1 className="sr-only">Detail Santri</h1>
      {isLoading && <Skeleton className="h-32 w-full" />}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat data santri.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && student !== undefined && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div className="min-w-0">
                <CardTitle className="truncate text-base">{student.fullName}</CardTitle>
                <p className="truncate text-xs text-muted-foreground">{student.email}</p>
              </div>
              {canManage && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditOpen(true);
                  }}
                >
                  Ubah Data
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <p className="text-sm capitalize text-muted-foreground">
                Status: {student.enrollmentStatus.toLowerCase()}
              </p>
              {student.guardianName !== null && (
                <p className="text-sm text-muted-foreground">Nama Wali: {student.guardianName}</p>
              )}
              {student.guardianPhone !== null && (
                <p className="text-sm text-muted-foreground">
                  Telepon Wali: {student.guardianPhone}
                </p>
              )}
              {canManage && (
                <div className="mt-2">
                  <DeactivateStudentButton studentId={student.id} />
                </div>
              )}
            </CardContent>
          </Card>

          <PreferredMethodCard student={student} />

          <TargetList studentId={student.id} />

          <ProgressSection studentId={student.id} />

          <QuranProgressMap studentId={student.id} />

          <SubmissionList studentId={student.id} />

          <SessionList studentId={student.id} />

          <MessageThreadCard studentId={student.id} />

          <GuardianSection studentId={student.id} />

          <EditStudentDialog student={student} open={editOpen} onOpenChange={setEditOpen} />
        </>
      )}
    </div>
  );
}
